/* eslint-disable  @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useContext } from 'react';
import { Form, Select, Typography, Input, Card, Row, Col } from 'antd';
import { IAuth, IUser, IUserDetails } from 'src/interfaces';
import { UPDATE_USER } from 'src/graphql';
import { useMutation } from '@apollo/client';
import { errorMessage, successMessage } from 'src/utils/Notifications';
import { UserContext } from 'src/utils/UserContext';
import { getToken, setToken } from 'src/services';
import { PrimaryButton } from '../components';

const { Title, Text } = Typography;
const FormItem = Form.Item;

type ContactInformationProps = {
    isAdmin: boolean;
    manageUser?: boolean;
    userDetails: IUserDetails;
    userId?: string;
    loading?: boolean;
};

function ContactInformation({
    isAdmin,
    manageUser,
    userDetails,
    userId,
    loading,
}: ContactInformationProps): JSX.Element {
    const [updateUser, { loading: updatingUser }] = useMutation(UPDATE_USER);
    const userContext = useContext(UserContext);
    const [form] = Form.useForm();
    const usaStates = [
        'Alabama',
        'Alaska',
        'Arizona',
        'Arkansas',
        'California',
        'Colorado',
        'Connecticut',
        'Delaware',
        'Florida',
        'Georgia',
        'Hawaii',
        'Idaho',
        'Illinois',
        'Indiana',
        'Iowa',
        'Kansas',
        'Kentucky',
        'Louisiana',
        'Maine',
        'Maryland',
        'Massachusetts',
        'Michigan',
        'Minnesota',
        'Mississippi',
        'Missouri',
        'Montana',
        'Nebraska',
        'Nevada',
        'New Hampshire',
        'New Jersey',
        'New Mexico',
        'New York',
        'North Carolina',
        'North Dakota',
        'Ohio',
        'Oklahoma',
        'Oregon',
        'Pennsylvania',
        'Rhode Island',
        'South Carolina',
        'South Dakota',
        'Tennessee',
        'Texas',
        'Utah',
        'Vermont',
        'Virginia',
        'Washington',
        'West Virginia',
        'Wisconsin',
        'Wyoming',
    ];

    useEffect(() => {
        form.setFieldsValue(userDetails);
    }, [userDetails]);

    const handleUpdate = (formData: IUserDetails) => {
        const { firstName, lastName, email, company, phone, fax, address, city, roleId, state, zip } = formData;
        updateUser({
            variables: {
                update: {
                    firstName,
                    lastName,
                    email,
                    company,
                    roleId,
                    phone,
                    fax,
                    address,
                    city,
                    state,
                    zip,
                },
                userId,
            },
        })
            .then(() => {
                successMessage('User details updated successfully');
                if (!manageUser) {
                    const data = getToken();
                    const newData: IAuth = {
                        ...data,
                        user: {
                            ...data.user,
                            firstName,
                            lastName,
                            email,
                            phone,
                        } as IUser,
                    };
                    setToken(newData);
                    userContext.setUserState(newData.user as IUser);
                }
            })
            .catch(() => {
                errorMessage('Failed to update user');
            });
    };

    return (
        <div
            style={{
                padding: '5px',
                marginLeft: '5px',
                marginRight: '5px',
                background: '#ffffff',
                borderRadius: '15px',
            }}
        >
            <Row>
                <Col span="24" style={{ paddingLeft: '10px', paddingTop: '10px' }}>
                    <Text className="card-heading">Contact Information</Text>
                </Col>
                <Col span="24" style={{ marginTop: '-27px', paddingLeft: '10px' }}>
                    <Text style={{ fontWeight: 'bold', fontSize: '20px', color: '#EE773B' }}>___</Text>
                </Col>
            </Row>
            <Card className="card" loading={loading}>
                <Form className="padding-top-lg" onFinish={handleUpdate} form={form}>
                    <Row>
                        <Col
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 12 }}
                            className="padding-right-sm"
                        >
                            <FormItem name="firstName">
                                <Input
                                    placeholder="First Name"
                                    style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                />
                            </FormItem>
                        </Col>
                        <Col
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 12 }}
                            className="padding-left-sm"
                        >
                            <FormItem name="lastName">
                                <Input
                                    placeholder="Last Name"
                                    style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                />
                            </FormItem>
                        </Col>
                    </Row>
                    <FormItem
                        name="email"
                        rules={[
                            { required: true, message: 'Email is required' },
                            { type: 'email', message: 'The input is not valid E-mail!' },
                        ]}
                    >
                        <Input
                            placeholder="Email"
                            type="email"
                            style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                        />
                    </FormItem>
                    <Row>
                        <Col
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 12 }}
                            className="padding-right-sm"
                        >
                            <FormItem name="phone">
                                <Input
                                    placeholder="Phone Number"
                                    style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                />
                            </FormItem>
                        </Col>
                        <Col
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 12 }}
                            className="padding-left-sm"
                        >
                            <FormItem name="fax">
                                <Input
                                    placeholder="Fax Number"
                                    style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                />
                            </FormItem>
                        </Col>
                    </Row>
                    <FormItem name="company" rules={[{ required: true, message: 'Company name is required' }]}>
                        <Input
                            placeholder="Company"
                            type="text"
                            style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                        />
                    </FormItem>
                    {(!isAdmin || manageUser) && (
                        <>
                            <FormItem name="address">
                                <Input
                                    placeholder="Office Address"
                                    style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                />
                            </FormItem>
                            <Row>
                                <Col
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 8 }}
                                    lg={{ span: 8 }}
                                    className="padding-right-sm"
                                >
                                    <FormItem name="city">
                                        <Input
                                            placeholder="City"
                                            style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                        />
                                    </FormItem>
                                </Col>
                                <Col
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 8 }}
                                    lg={{ span: 8 }}
                                    className="padding-x-sm"
                                >
                                    <Form.Item name="state">
                                        <Select
                                            placeholder="State"
                                            style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                        >
                                            {usaStates.map((stateName) => (
                                                <Select.Option key={stateName} value={stateName}>
                                                    {stateName}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 8 }}
                                    lg={{ span: 8 }}
                                    className="padding-left-sm"
                                >
                                    <FormItem name="zip">
                                        <Input
                                            placeholder="Zip Code"
                                            style={{ borderWidth: '1px', borderColor: '#EE773B', borderRadius: '50px' }}
                                        />
                                    </FormItem>
                                </Col>
                            </Row>
                        </>
                    )}
                    {isAdmin && (
                        <>
                            <FormItem style={{ marginRight: '5px' }} name="roleId">
                                <Select className="full-width" placeholder="Select Role" style={{ height: '40px' }}>
                                    <Select.Option value="ADMIN">Admin</Select.Option>
                                    <Select.Option value="CLIENT">Client</Select.Option>
                                </Select>
                            </FormItem>
                        </>
                    )}
                    <div className="margin-bottom-lg" style={{ textAlign: 'center' }}>
                        <PrimaryButton title="Save" htmlType="submit" loading={updatingUser} />
                    </div>
                </Form>
            </Card>
        </div>
    );
}

export default ContactInformation;
